import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import {
  House, Buildings, CalendarBlank, ChatCircle, Wallet, MagnifyingGlass, Binoculars,
  GearSix, List, SignOut, CheckSquare, Plugs, Lightning, CurrencyInr, Receipt,
  Moon, Sun, Crown, Storefront, Scales, ShieldCheck, ArrowsLeftRight, UserCircle, Funnel
} from "@phosphor-icons/react";
import { useProperties, useUnreadCount } from "@/lib/queries";
import { usePropertyFilter } from "@/context/PropertyFilter";
import logoH from "@/assets/logo_h_3d.png";
import { roleBadge } from "@/components/PermissionEditor";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navItems = [
  { path: "/dashboard",      icon: House,           label: "Home" },
  { path: "/properties",     icon: Buildings,       label: "Properties" },
  { path: "/calendar",       icon: CalendarBlank,   label: "Bookings" },
  { path: "/inbox",          icon: ChatCircle,      label: "Inbox" },
  { path: "/tasks",          icon: CheckSquare,     label: "Tasks" },
  { path: "/pricing",        icon: Lightning,       label: "Pricing" },
  { path: "/expenses",       icon: Wallet,          label: "Expenses" },
  { path: "/evaluator",      icon: MagnifyingGlass, label: "Evaluator" },
  { path: "/finder",         icon: Binoculars,      label: "Finder" },
  { path: "/gst",            icon: Receipt,         label: "GST" },
  { path: "/setup-advisor",  icon: Storefront,      label: "Setup Advisor" },
  { path: "/legal-guide",    icon: Scales,          label: "Legal Guide" },
  { path: "/billing",        icon: CurrencyInr,     label: "Billing" },
  { path: "/integrations",   icon: Plugs,           label: "Integrations" },
  { path: "/settings",       icon: GearSix,         label: "Settings" },
];

// mobileNavItems/mobileMoreItems computed inside component for staff filtering

const pageVariants = {
  initial: { opacity: 0, y: 18, scale: 0.994 },
  enter: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.34, ease: [0.34, 1.10, 0.64, 1] },
  },
  exit: {
    opacity: 0, y: -10, scale: 0.996,
    transition: { duration: 0.20, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

function permLevel(user, pageKey) {
  if (!user?.is_staff) return "edit";
  const p = user.permissions || {};
  if (Array.isArray(p)) return p.includes(pageKey) ? "edit" : "none";
  return p[pageKey] || "none";
}

export default function Layout() {
  const { user, logout, switchWorkspace } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { selectedPropertyId, setSelectedPropertyId, selectedRoomId, setSelectedRoomId } = usePropertyFilter();
  const { data: properties = [] } = useProperties();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);

  const visibleNavItems = user?.is_staff
    ? navItems.filter(item => permLevel(user, item.path.slice(1)) !== "none")
    : navItems;
  const mobileNavItems = visibleNavItems.slice(0, 4);
  const { data: unread = 0 } = useUnreadCount(visibleNavItems.some(i => i.path === "/inbox"));
  const badgeFor = (path) => (path === "/inbox" && unread > 0 ? (unread > 99 ? "99+" : String(unread)) : null);
  const mobileMoreItems = visibleNavItems.slice(4);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 font-outfit">

      {/* â"€â"€ Left Sidebar (desktop only) â"€â"€ */}
      <aside className="hidden md:flex flex-col w-56 shrink-0 bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 shadow-sm sticky top-0 h-screen">
        {/* Logo */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <motion.div
            className="flex items-center gap-1 select-none"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            data-testid="app-logo"
          >
            <img src={logoH} alt="HostFolio" className="w-10 h-10 object-contain" />
            <h1 className="font-manrope text-xl font-bold tracking-tight">
              <span className="text-slate-800 dark:text-white">Host</span>
              <span className="text-amber-500">Folio</span>
            </h1>
          </motion.div>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" data-testid="desktop-nav">
          {visibleNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`desktop-nav-${item.label.toLowerCase()}`}
              >
                {() => (
                  <motion.div
                    className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors ${
                      active
                        ? "text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400"
                        : "text-slate-500 dark:text-slate-200 hover:text-slate-700 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    }`}
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  >
                    {active && (
                      <motion.div
                        layoutId="sidebar-pill"
                        className="absolute inset-0 bg-amber-50 rounded-xl"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-3">
                      <item.icon
                        size={17}
                        weight={active ? "duotone" : "regular"}
                        className={active ? "text-amber-500" : "text-slate-400 dark:text-slate-300"}
                      />
                      {item.label}
                    </span>
                    {badgeFor(item.path) && (
                      <span className="relative z-10 ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
                        aria-label={`${unread} unread messages`} data-testid="inbox-unread-badge">
                        {badgeFor(item.path)}
                      </span>
                    )}
                    {active && (
                      <motion.div
                        layoutId="sidebar-bar"
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-amber-400 rounded-r-full"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </motion.div>
                )}
              </NavLink>
            );
          })}

          {/* Admin-only nav item */}
          {user?.plan === "admin" && (() => {
            const active = isActive("/admin");
            return (
              <>
                <div className="px-3 pt-3 pb-1">
                  <div className="h-px bg-red-100 dark:bg-red-900/30" />
                </div>
                <NavLink to="/admin">
                  {() => (
                    <motion.div
                      className={`relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium cursor-pointer transition-colors ${
                        active
                          ? "text-red-600 bg-red-50 dark:bg-red-950/30 dark:text-red-400"
                          : "text-red-400 dark:text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                      }`}
                      whileHover={{ x: 2 }}
                      whileTap={{ scale: 0.97 }}
                      transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    >
                      <ShieldCheck size={17} weight={active ? "duotone" : "regular"} />
                      Admin
                    </motion.div>
                  )}
                </NavLink>
              </>
            );
          })()}
        </nav>

        {/* Workspace switcher — shown when user has co-host memberships */}
        {(user?.workspaces?.length > 0 || user?.is_staff) && (
          <div className="px-3 pb-1">
            <div className="relative">
              <button
                onClick={() => setWorkspaceOpen(o => !o)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium bg-slate-50 dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 border border-slate-200 dark:border-slate-700 transition-colors"
              >
                <ArrowsLeftRight size={13} className="text-amber-500 shrink-0" />
                <span className="truncate text-slate-600 dark:text-slate-300">
                  {user?.is_staff ? `Co-hosting for ${user?.owner_name || "Host"}` : "My Account"}
                </span>
              </button>
              {workspaceOpen && (
                <div className="absolute bottom-full mb-1 left-0 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-50 overflow-hidden">
                  {user?.is_staff && (
                    <button
                      onClick={() => { switchWorkspace(null); setWorkspaceOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <UserCircle size={13} className="text-slate-400 shrink-0" />
                      <span className="text-slate-700 dark:text-slate-200 font-medium">My Account</span>
                    </button>
                  )}
                  {(user?.workspaces || []).map(ws => (
                    <button
                      key={ws.staff_id}
                      onClick={() => { switchWorkspace(ws.staff_id); setWorkspaceOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <ArrowsLeftRight size={13} className="text-amber-400 shrink-0" />
                      <div>
                        <p className="text-slate-700 dark:text-slate-200 font-medium">{ws.owner_name}'s properties</p>
                        <p className="text-slate-400 capitalize">{ws.role.replace("_", " ")}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Global property / room filter */}
        {properties.length > 0 && (
          <div className="px-3 pb-2">
            <div className="flex items-center gap-1.5 px-2 py-1 mb-1">
              <Funnel size={11} className="text-slate-400 shrink-0" />
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Filter</span>
            </div>
            <select
              value={selectedRoomId ? `${selectedPropertyId}::${selectedRoomId}` : (selectedPropertyId || "__all__")}
              onChange={e => {
                const val = e.target.value;
                if (val === "__all__") { setSelectedPropertyId(""); setSelectedRoomId(""); return; }
                if (val.includes("::")) {
                  const [pid, rid] = val.split("::");
                  setSelectedPropertyId(pid);
                  setSelectedRoomId(rid);
                } else {
                  setSelectedPropertyId(val);
                  setSelectedRoomId("");
                }
              }}
              className="w-full text-xs min-h-[44px] rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2.5 py-2 focus:outline-none focus:border-amber-400 transition-colors"
            >
              <option value="__all__">All properties</option>
              {properties.map(p => p.unit_type === "hotel" ? (
                <optgroup key={p.id} label={p.name}>
                  <option value={p.id}>{p.name} (all rooms)</option>
                  {(p.rooms || []).map(r => (
                    <option key={r.room_id} value={`${p.id}::${r.room_id}`}>
                      {"  "}{r.name}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {(selectedPropertyId || selectedRoomId) && (
              <button
                onClick={() => { setSelectedPropertyId(""); setSelectedRoomId(""); }}
                className="w-full mt-1 text-[10px] text-slate-400 hover:text-amber-500 transition-colors"
              >
                Clear filter
              </button>
            )}
          </div>
        )}

        {/* User area at bottom */}
        <div className="px-3 py-3 border-t border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
            <Avatar className="h-7 w-7 ring-2 ring-amber-100 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {user?.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{user?.name}</p>
              <p className="text-[10px] text-slate-400 dark:text-slate-300 truncate">{user?.email}</p>
              {user?.is_staff ? (
                <span className={`inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  user.role === "co_host"
                    ? "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
                    : user.role === "manager"
                    ? "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-300"
                    : "bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300"
                }`}>
                  {roleBadge(user.role)}
                </span>
              ) : user?.plan && (
                <span className={`inline-block mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${
                  user.plan === "agency"  ? "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" :
                  user.plan === "pro"     ? "bg-amber-100  text-amber-600  dark:bg-amber-900/40  dark:text-amber-300"  :
                  user.plan === "starter" ? "bg-blue-100   text-blue-600   dark:bg-blue-900/40   dark:text-blue-300"   :
                  user.plan === "admin"   ? "bg-red-100    text-red-600    dark:bg-red-900/40    dark:text-red-300"    :
                                            "bg-slate-100  text-slate-500  dark:bg-slate-700     dark:text-slate-400"
                }`}>
                  {user.plan === "free"
                    ? "Trial"
                    : (user.plan_is_trial && user.plan_expires_at)
                      ? `${user.plan} · ${Math.max(0, Math.ceil((new Date(user.plan_expires_at) - Date.now()) / 86400000))}d trial`
                      : user.plan}
                </span>
              )}
            </div>
            <motion.button
              onClick={logout}
              className="text-slate-400 hover:text-red-400 dark:text-slate-200 dark:hover:text-red-400 transition-colors p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Sign out"
              data-testid="logout-button"
            >
              <SignOut size={15} />
            </motion.button>
          </div>
        </div>
      </aside>

      {/* â"€â"€ Main content area â"€â"€ */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Desktop-only top header */}
        <header className="hidden md:flex items-center justify-between h-13 px-8 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm sticky top-0 z-40">
          {/* Left: greeting */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">Welcome back,</span>
            <span className="text-sm font-semibold text-slate-800 dark:text-white">{user?.name?.split(" ")[0]}</span>
            {user?.is_staff ? (
              <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                user.role === "co_host"
                  ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                  : user.role === "manager"
                  ? "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800"
                  : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
              }`}>
                {roleBadge(user.role)}
              </span>
            ) : user?.plan && user.plan !== "free" && (
              <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 uppercase tracking-wide">
                <Crown size={9} weight="fill" />
                {user.plan}
              </span>
            )}
          </div>
          {/* Right: dark mode toggle */}
          <motion.button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 hover:text-amber-600 dark:hover:text-amber-400 bg-white dark:bg-slate-800 hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:border-amber-200 dark:hover:border-amber-800 transition-all shadow-sm"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.94 }}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={theme}
                initial={{ opacity: 0, rotate: -20, scale: 0.7 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 20, scale: 0.7 }}
                transition={{ duration: 0.18 }}
                className="flex items-center gap-2"
              >
                {theme === "dark"
                  ? <Sun size={15} weight="duotone" className="text-amber-400" />
                  : <Moon size={15} weight="duotone" className="text-slate-500" />}
                <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
              </motion.span>
            </AnimatePresence>
          </motion.button>
        </header>

        {/* Mobile-only top bar */}
        <header className="md:hidden glass-header sticky top-0 z-50">
          <div className="h-14 flex items-center justify-between px-4">
            <motion.h1
              className="font-manrope text-lg font-bold tracking-tight select-none"
              data-testid="app-logo"
            >
              <span className="text-slate-800 dark:text-white">Host</span>
              <span className="text-amber-500">Folio</span>
            </motion.h1>
            <div className="flex items-center gap-2">
              {user?.is_staff ? (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300 capitalize">
                  {roleBadge(user.role)}
                </span>
              ) : user?.plan && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full capitalize ${
                  user.plan === "agency"  ? "bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300" :
                  user.plan === "pro"     ? "bg-amber-100  text-amber-600  dark:bg-amber-900/40  dark:text-amber-300"  :
                  user.plan === "starter" ? "bg-blue-100   text-blue-600   dark:bg-blue-900/40   dark:text-blue-300"   :
                  user.plan === "admin"   ? "bg-red-100    text-red-600    dark:bg-red-900/40    dark:text-red-300"    :
                                            "bg-slate-100  text-slate-500  dark:bg-slate-700     dark:text-slate-400"
                }`}>
                  {user.plan === "free"
                    ? "Trial"
                    : (user.plan_is_trial && user.plan_expires_at)
                      ? `${user.plan} · ${Math.max(0, Math.ceil((new Date(user.plan_expires_at) - Date.now()) / 86400000))}d`
                      : user.plan}
                </span>
              )}
              <motion.button
                onClick={toggleTheme}
                className="text-slate-400 hover:text-amber-500 dark:text-slate-300 dark:hover:text-amber-400 transition-colors min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                whileTap={{ scale: 0.9 }}
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              </motion.button>
              <Avatar className="h-8 w-8 ring-2 ring-amber-100">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                  {user?.name?.charAt(0) || "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>

          {/* Mobile global property / room filter (mirrors the desktop sidebar filter) */}
          {properties.length > 0 && (
            <div className="px-4 pb-2 flex items-center gap-2">
              <Funnel size={13} className="text-slate-400 shrink-0" />
              <select
                value={selectedRoomId ? `${selectedPropertyId}::${selectedRoomId}` : (selectedPropertyId || "__all__")}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "__all__") { setSelectedPropertyId(""); setSelectedRoomId(""); return; }
                  if (val.includes("::")) {
                    const [pid, rid] = val.split("::");
                    setSelectedPropertyId(pid);
                    setSelectedRoomId(rid);
                  } else {
                    setSelectedPropertyId(val);
                    setSelectedRoomId("");
                  }
                }}
                className="flex-1 min-w-0 text-xs min-h-[44px] rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2.5 py-2 focus:outline-none focus:border-amber-400 transition-colors"
              >
                <option value="__all__">All properties</option>
                {properties.map(p => p.unit_type === "hotel" ? (
                  <optgroup key={p.id} label={p.name}>
                    <option value={p.id}>{p.name} (all rooms)</option>
                    {(p.rooms || []).map(r => (
                      <option key={r.room_id} value={`${p.id}::${r.room_id}`}>
                        {"  "}{r.name}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {(selectedPropertyId || selectedRoomId) && (
                <button
                  onClick={() => { setSelectedPropertyId(""); setSelectedRoomId(""); }}
                  className="text-[10px] text-slate-400 hover:text-amber-500 transition-colors shrink-0"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 pb-20 md:pb-8 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={location.pathname}
                variants={pageVariants}
                initial="initial"
                animate="enter"
                exit="exit"
                style={{ willChange: "transform, opacity" }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* â"€â"€ Mobile bottom nav â"€â"€ */}
      <nav
        className="fixed bottom-0 left-0 right-0 glass-nav z-50 md:hidden pb-safe shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
        data-testid="mobile-nav"
      >
        <div className="flex items-center justify-around h-16">
          {mobileNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {() => (
                  <motion.div
                    className={`flex flex-col items-center gap-0.5 px-3 py-1 ${active ? "text-amber-500" : "text-slate-400"}`}
                    whileTap={{ scale: 0.88 }}
                    transition={{ type: "spring", stiffness: 500, damping: 20 }}
                  >
                    <motion.div
                      className="relative"
                      animate={active ? { scale: 1.15, y: -1 } : { scale: 1, y: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    >
                      <item.icon size={22} weight={active ? "duotone" : "regular"} />
                      {badgeFor(item.path) && (
                        <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-slate-900"
                          aria-label={`${unread} unread messages`} data-testid="mobile-inbox-unread-badge">
                          {badgeFor(item.path)}
                        </span>
                      )}
                    </motion.div>
                    <span className="text-[10px] font-medium">{item.label}</span>
                  </motion.div>
                )}
              </NavLink>
            );
          })}

          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <motion.button
                className={`flex flex-col items-center gap-0.5 px-3 py-1 ${
                  mobileMoreItems.some(i => isActive(i.path)) ? "text-amber-500" : "text-slate-400"
                }`}
                whileTap={{ scale: 0.88 }}
                data-testid="nav-more"
              >
                <List size={22} />
                <span className="text-[10px] font-medium">More</span>
              </motion.button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-t-2xl shadow-xl">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="py-2 space-y-1">
                {mobileMoreItems.map((item, i) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.25, ease: [0.34, 1.10, 0.64, 1] }}
                  >
                    <NavLink
                      to={item.path}
                      onClick={() => setMoreOpen(false)}
                      className={() =>
                        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                          isActive(item.path)
                            ? "bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400"
                            : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        }`
                      }
                    >
                      <item.icon size={20} weight="regular" />
                      <span className="text-sm">{item.label}</span>
                    </NavLink>
                  </motion.div>
                ))}
                {user?.plan === "admin" && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMoreOpen(false)}
                    className={() =>
                      `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 font-medium ${
                        isActive("/admin")
                          ? "bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400"
                          : "text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20"
                      }`
                    }
                  >
                    <ShieldCheck size={20} weight="regular" />
                    <span className="text-sm">Admin</span>
                  </NavLink>
                )}
                {/* Workspace switcher — mirrors the desktop sidebar switcher */}
                {(user?.workspaces?.length > 0 || user?.is_staff) && (
                  <div className="mt-1 pt-1 border-t border-slate-100 dark:border-slate-800">
                    {user?.is_staff && (
                      <button
                        onClick={() => { setMoreOpen(false); switchWorkspace(null); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl w-full text-left text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <UserCircle size={20} className="text-slate-400 shrink-0" />
                        <span className="text-sm font-medium">Switch to my account</span>
                      </button>
                    )}
                    {(user?.workspaces || []).map(ws => (
                      <button
                        key={ws.staff_id}
                        onClick={() => { setMoreOpen(false); switchWorkspace(ws.staff_id); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl w-full text-left text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <ArrowsLeftRight size={20} className="text-amber-400 shrink-0" />
                        <span className="text-sm">
                          View as <span className="font-medium">{ws.owner_name}'s properties</span>
                          <span className="text-slate-400 capitalize"> · {ws.role.replace("_", " ")}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <motion.button
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: mobileMoreItems.length * 0.04, duration: 0.25 }}
                  onClick={() => { setMoreOpen(false); logout(); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 w-full transition-colors"
                  data-testid="mobile-logout"
                >
                  <SignOut size={20} />
                  <span className="text-sm font-medium">Sign Out</span>
                </motion.button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </div>
  );
}
